import { TableName } from '../../../../../tables'
import { UIPermission, DetailPanel, DetailFieldProps, FieldType, FieldValue, Action, Tab, useGetCrumbs, PDFTabContent, ModalId, useUIActions } from '../../../../../features/ui'
import { useMemo, useState } from 'react'
import { PriceBreakdown, PaymentStatus, usePriceOutlineHooks, operationHelpers } from '../../../../operations'
import { CompanyUserName, useSystemStateHooks } from '../../../../system'
import { PurchaseOrder } from '../../../state/types'
import { RoutePath } from '../../../../../features/routes'
import { Labels, useDescribeHooks, DirectArchiveModal, useDataTypesHooks } from '../../../../../features/data-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { UploadAdditionalTicketModal, TicketList, Ticket, DownloadTicketModal } from '../../../../administration'
import { purchaseHelpers, usePurchaseHooks } from '../../../hooks/purchaseHooks'
import { PurchaseItemTable } from '../purchase-item-table/purchaseItemTable'
import { Delivery, DeliveryOrder, DeliveryOrdersDataList, DeliveryStatus, DownloadDeliveryNoteModal, UploadDeliveryNoteModal, useDeliveryHooks } from '../../../../deliveries'
import { useBranchLoaders } from '../../../../manager'
import { addressHelpers } from '../../../../../features/address'
import { toDate } from '../../../../../utils/typeUtils'
import { PurchaseDocumentsPanel } from '../documents-panel/purchaseDocumentsPanel'
import { DownloadPurchaseFilesModal } from '../download-purchase-files-modal/downloadPurchaseFilesModal'

type Props = {
  purchase: PurchaseOrder
  deliveryOrders: DeliveryOrder[]
  deliveries: Delivery[]
  tickets: Ticket[]
}

export const PurchaseDetail = (props: Props) => {
  const { purchase, deliveryOrders, tickets } = props
  const { items, deliveryData, ...data } = purchase

  const [deliveries, setDeliveries] = useState<Delivery[]>(props.deliveries)
  const [currentTicket, setCurrentTicket] = useState<Ticket>()
  const [currentDelivery, setCurrentDelivery] = useState<Delivery>()
  
  const { describeCode } = operationHelpers
  const { isArchivable } = purchaseHelpers
  const { describeAddress } = addressHelpers
  const { isEditableSync, isInvoiceableSync } = usePurchaseHooks()
  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_PURCHASES) || purchase.archived
  const { describe } = useDescribeHooks()
  const { refreshDeliveries } = useDeliveryHooks()
  const { getPriceOutline } = usePriceOutlineHooks()
  const { formatDate, formatDateTime } = useDataTypesHooks()
  const navigate = useNavigate()
  const locationFrom = useLocation().state?.from

  const branchLoaders = useBranchLoaders()

  const { openModal } = useUIActions()

  const title = describe(TableName.PURCHASE_ORDERS, purchase)
  const crumbs = useGetCrumbs(
    `${locationFrom ? 'Recepción de ' : ''}Compras`,
    locationFrom || RoutePath.PURCHASES,
    describeCode(purchase)
  )

  const findDeliveryOrder = (delivery?: Delivery) => deliveryOrders.find(order => order.id === delivery?.orderId)

  const currentDeliveryOrder = useMemo(() => findDeliveryOrder(currentDelivery), [currentDelivery])
  const address = useMemo(() => branchLoaders().findBranch(purchase.deliveryBranchId)!.address, [purchase.deliveryBranchId])
  const priceOutline = useMemo(() => getPriceOutline(TableName.PURCHASE_ORDERS, purchase), [purchase])

  const fields: DetailFieldProps[] = [
    {
      name: 'supplierId',
      type: FieldType.TABLE,
      table: TableName.SUPPLIERS,
      label: 'Proveedor'
    },
    {
      name: 'items',
      type: FieldType.TABLE,
      render: () => (
        <PurchaseItemTable
          itemsData={items}
          createMode={false}
          readOnly
        />
      )
    },
    {
      name: 'deliveryData',
      type: FieldType.TEXT,
      label: 'Datos de Entrega',
      render: () => (
        <>
          {Labels.deliveryMethodLabel(deliveryData!.method)}
          {" - " + describeAddress(address)}
          {" - " + formatDate(toDate(deliveryData!.deliveryDate))}
        </>
      )
    },
    {
      name: 'priceBreakdown',
      type: FieldType.PRICE,
      label: 'Precio',
      loading: items.length === 0,
      render: () => <PriceBreakdown
        data={priceOutline}
        discountLabel="Bonificaciones"
      />
    },
    {
      name: 'paymentStatus',
      type: FieldType.TEXT,
      label: 'Estado de Pago',
      render: (value?: FieldValue) => <>{Labels.paymentStatusLabel(value! as PaymentStatus)}</>
    },
    {
      name: 'deliveryStatus',
      type: FieldType.TEXT,
      label: 'Estado de Entrega',
      render: (value?: FieldValue) => <>{Labels.deliveryStatusLabel(value! as DeliveryStatus)}</>
    }
  ]

  if (purchase.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = purchase.negativeStateAt ? ` - ${formatDateTime(new Date(purchase.negativeStateAt))}` : ''
        return <><CompanyUserName userId={purchase.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const onDownloadFiles = () => openModal(ModalId.DOWNLOAD_FILES)

  const onEdit = () => navigate(RoutePath.EDIT)

  const onUploadTicket = () => {
    if (tickets.length > 0) {
      openModal(ModalId.UPLOAD_ADDITIONAL_TICKET)
    } else {
      navigate(`${RoutePath.TICKETS}/${RoutePath.CREATE}`)
    }
  }

  const onArchive = () => openModal(ModalId.DIRECT_ARCHIVE)

  const actions: Action[] = useMemo(() => {
    const actionList: Action[] = [
      { label: 'Documentos', kind: 'tertiary', icon: 'download', onClick: onDownloadFiles }
    ]

    if (!readOnly) {
      if (isEditableSync(purchase)) {
        actionList.push({ label: 'Editar Compra', icon: 'pencil-fill', onClick: onEdit })
      }
      if (isInvoiceableSync(purchase)) {
        actionList.push({ label: 'Subir Factura', icon: 'upload', onClick: onUploadTicket })
      }
      if (isArchivable(purchase)) {
        actionList.push({ label: 'Archivar', kind: 'secondary', icon: 'archive-fill', onClick: onArchive })
      }
    }

    return actionList
  }, [purchase.deliveryStatus, purchase.paymentStatus, tickets, deliveryOrders, deliveries])

  const tabs = useMemo(() => {
    const tabList: Tab[] = [
      {
        id: 'documents',
        title: 'Documentos',
        content: (
          <PurchaseDocumentsPanel
            purchase={purchase}
            deliveries={deliveries}
            deliveryOrders={deliveryOrders}
            tickets={tickets}
            onDownloadTicket={setCurrentTicket}
            onDownloadDeliveryNote={setCurrentDelivery}
          />
        )
      }
    ]
    if (tickets.length > 0) {
      tabList.push({
        id: 'tickets',
        title: 'Facturas',
        content: <TicketList
          tickets={tickets}
          readOnly={readOnly}
          onChangeCurrentTicket={setCurrentTicket}
        />
      })
    }
    tabList.push({
      id: 'deliveryOrders',
      title: 'Entregas',
      content: <DeliveryOrdersDataList
        deliveryData={deliveryData}
        deliveryOrders={deliveryOrders}
        deliveries={deliveries}
        operation={purchase}
        branchId={purchase.deliveryBranchId}
        disableEdit
        onChangeCurrentDelivery={setCurrentDelivery}
      />
    })

    return tabList
  }, [purchase, deliveryData, deliveryOrders, deliveries, tickets])

  const onUploadDeliveryNote = async () => {
    const refreshedDeliveries = await refreshDeliveries(deliveries)
    setDeliveries(refreshedDeliveries)
  }

  return (<>
    <DetailPanel
      tableName={TableName.PURCHASE_ORDERS}
      title={title}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='cart3'
      imgSpace={3}
      tabs={tabs}
      readOnly={readOnly}
      actions={actions}
    />
    <DownloadPurchaseFilesModal purchase={purchase} />
    <DownloadTicketModal ticket={currentTicket} />
    <DownloadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={currentDeliveryOrder}
    />
    <UploadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={currentDeliveryOrder}
      onUpload={onUploadDeliveryNote}
    />
    <UploadAdditionalTicketModal />
    <DirectArchiveModal
      tableName={TableName.PURCHASE_ORDERS}
      dataType={purchase}
    />
  </>)
}
